import Button from '@shared/components/core/button/CoreButton';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { FlexPageContent, SubSectionContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import SplashLoader from '@shared/components/loading/SplashLoader';
import { useCurrentOrgAuthClientEditor } from '@shared/hooks/useCurrentOrgAuthClientEditor';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import useCurrentOrgProjectsFetching from '@shared/hooks/useCurrentOrgProjectsFetching';
import DeviceTypeModel from '@shared/models/devices/DeviceTypeModel';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import OrgNoAccessBanner from '../../banners/OrgNoAccessBanner';
import ProjectsUpgradeBanner from '../ProjectsUpgradeBanner';
import ClientAuthRedirectEditor from './editors/ClientAuthRedirectEditor';
import ClientDeviceTypeEditor from './editors/ClientDeviceTypeEditor';
import ClientKeyEditor from './editors/ClientKeyEditor';
import ClientNameEditor from './editors/ClientNameEditor';
import ClientOriginHostEditor from './editors/ClientOriginHostEditor';
import ClientScopeEditor from './editors/ClientScopeEditor';
import ClientTokenRedirectEditor from './editors/ClientTokenRedirectEditor';

export default function ProjectClientPageContent() {
    const { t } = useTranslation();
    const { organizationId, clientId } = useParams();
    const { currentOrg, canCreateAuthClients, isLoading } = useCurrentOrgDetailsFetching();
    const { deviceTypes, currentOrgClient, error, isLoading: isLoadingProjects } = useCurrentOrgProjectsFetching();
    const {
        editClient,
        handleInputChange,
        handleSaveWithAlerts: handleSave,
    } = useCurrentOrgAuthClientEditor(currentOrgClient);

    if (!currentOrgClient || !deviceTypes || !editClient || isLoadingProjects || isLoading) return <SplashLoader />;

    if (
        !organizationId ||
        !clientId ||
        !currentOrg ||
        error.client ||
        !canCreateAuthClients ||
        organizationId !== currentOrg.id
    )
        return <OrgNoAccessBanner />;

    if (!currentOrg.isDeveloperAccess || !currentOrg.canCreateAuthClients) return <ProjectsUpgradeBanner />;

    return (
        <FlexPageContent>
            <SubSectionRow title={t('common:organization.projects.key.title')} />
            <SubSectionContent>
                <ClientKeyEditor organizationId={organizationId} clientId={currentOrgClient.id} />
            </SubSectionContent>
            <SubSectionRow title={t('common:organization.projects.clients.settings')} />
            <SubSectionContent>
                <ClientNameEditor
                    value={editClient.name}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <ClientOriginHostEditor
                    value={editClient.originHost}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <ClientTokenRedirectEditor
                    value={editClient.tokenAdditionalRedirectUris}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <ClientAuthRedirectEditor
                    value={editClient.additionalRedirectUris}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
            </SubSectionContent>
            <SubSectionRow title={t('common:organization.projects.scopes.title')} />
            <SubSectionContent>
                <ClientScopeEditor
                    value={editClient.authClientScopes}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
            </SubSectionContent>
            <SubSectionRow title={t('common:organization.projects.deviceScopes.title')} />
            <SubSectionContent>
                <ClientDeviceTypeEditor
                    value={editClient.authClientDeviceTypes}
                    deviceOptions={deviceTypes ?? ([] as DeviceTypeModel[])}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
            </SubSectionContent>
            <Button onClick={handleSave}>{t('common:save')}</Button>
        </FlexPageContent>
    );
}
